import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AiResultDialog, useAiRunner } from "@/components/ai/AiResultDialog";
import { ScrollText, GitCompareArrows, HelpCircle, Loader2, Send, Lock } from "lucide-react";

export default function AiAssistant() {
  const { t, lang } = useLang();
  const { ai } = useAuth();
  const { state, run, close } = useAiRunner();
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [asking, setAsking] = useState(false);

  const available = !!(ai?.available && ai?.canUse);

  const ask = async (override) => {
    const q = (override ?? question).trim();
    if (!q) return;
    setAsking(true);
    // Send the last few turns so the assistant can handle natural follow-ups.
    const priorTurns = [...history].slice(0, 3).reverse().flatMap((h) =>
      h.error ? [] : [{ role: "user", content: h.q }, { role: "assistant", content: h.a }]);
    try {
      const { data } = await api.post("/ai/help", { question: q, language: lang, history: priorTurns });
      setHistory((h) => [{ q, a: data.content || "", sources: data.sources || [] }, ...h]);
      if (override == null) setQuestion("");
    } catch (e) {
      setHistory((h) => [{ q, a: "", error: apiErr(e) }, ...h]);
    } finally { setAsking(false); }
  };

  const SUGGESTIONS = ["ai.suggest1", "ai.suggest2", "ai.suggest3", "ai.suggest4"];

  if (!available) {
    return (
      <>
        <PageHeader title={t("ai.title")} subtitle={t("ai.subtitle")} />
        <div className="mx-4 flex flex-col items-center gap-3 rounded-sm border border-zinc-200 bg-white px-6 py-16 text-center" data-testid="ai-unavailable">
          <Lock className="text-zinc-300" size={32} />
          <div className="text-sm font-bold text-zinc-800">{t("ai.notAvailableTitle")}</div>
          <p className="max-w-md text-sm text-zinc-500">{t("ai.notAvailable")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t("ai.title")} subtitle={t("ai.subtitle")} />
      <div className="space-y-6 px-4 pb-10">
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400">{t("ai.quickActions")}</div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => run(t("ai.summaryTitle"), "/ai/summarize-actions", { language: lang, limit: 60 })}
              data-testid="ai-quick-summarize" className="group flex items-center gap-3 rounded-sm border border-zinc-200 bg-white px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5">
              <ScrollText size={18} className="text-primary" />
              <div><div className="text-sm font-semibold text-zinc-900">{t("ai.summaryTitle")}</div><div className="text-xs text-zinc-500">{t("ai.summaryDesc")}</div></div>
            </button>
            <button onClick={() => run(t("ai.driftTitle"), "/ai/explain-drift", { language: lang })}
              data-testid="ai-quick-drift" className="group flex items-center gap-3 rounded-sm border border-zinc-200 bg-white px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5">
              <GitCompareArrows size={18} className="text-primary" />
              <div><div className="text-sm font-semibold text-zinc-900">{t("ai.driftTitle")}</div><div className="text-xs text-zinc-500">{t("ai.driftDesc")}</div></div>
            </button>
          </div>
        </div>

        <div className="rounded-sm border border-zinc-200 bg-white p-5">
          <div className="mb-1 flex items-center gap-2 text-sm font-bold text-zinc-900"><HelpCircle size={16} className="text-primary" /> {t("ai.helpTitle")}</div>
          <p className="mb-3 text-xs text-zinc-500">{t("ai.helpDesc")}</p>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t("ai.helpPlaceholder")} data-testid="ai-help-input" className="rounded-sm border-zinc-300" />
            <Button type="submit" disabled={asking || !question.trim()} data-testid="ai-help-ask" className="rounded-sm">
              {asking ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </Button>
          </form>
          <p className="mt-2 text-[11px] text-zinc-400">{t("ai.disclaimer")}</p>
          {history.length === 0 && (
            <div className="mt-4" data-testid="ai-suggestions">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-zinc-400">{t("ai.suggestHeading")}</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((key, i) => (
                  <button key={key} onClick={() => ask(t(key))} disabled={asking} data-testid={`ai-suggestion-${i}`}
                    className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-600 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:opacity-50">
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 space-y-4" data-testid="ai-help-history">
            {history.length === 0 && <div className="py-6 text-center text-sm text-zinc-400">{t("ai.helpEmpty")}</div>}
            {history.map((h, i) => (
              <div key={i} className="rounded-sm border border-zinc-100 bg-zinc-50 p-3" data-testid={`ai-help-answer-${i}`}>
                <div className="mb-1.5 text-sm font-semibold text-zinc-800">{h.q}</div>
                {h.error ? <div className="text-sm text-rose-600">{h.error}</div> :
                  <div className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-700">{h.a}</div>}
                {h.sources?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {h.sources.map((sc, j) => (<span key={j} className="border border-zinc-200 bg-white px-2 py-0.5 font-mono text-[10px] text-zinc-500">{sc.file}</span>))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <AiResultDialog open={state.open} onOpenChange={(v) => !v && close()} title={state.title} loading={state.loading} error={state.error} content={state.content} sources={state.sources} empty={state.empty} />
    </>
  );
}
